import { AssetDto, BynderMetadataDto, ProducerDto } from "generated";
import { EXCLUDED_SECTIONS, metadataSections, SELECT_ALL } from "./constants";
import { MetadataEntry, MetadataSection, ModalState } from "./types";
import { ProducedByDto } from "@/hooks/types";

export const applyTransformation = (
  rawValue: AssetDto[keyof AssetDto],
  transformation: MetadataSection["transformation"],
  producers?: ProducerDto[],
  producedBy?: ProducedByDto,
  metadata?: BynderMetadataDto,
  documentContentTypes?: Record<string, string>,
) => {
  if (!transformation) {
    return rawValue;
  }
  return Array.isArray(rawValue)
    ? rawValue.map((v) =>
        transformation(
          v,
          producers,
          producedBy,
          metadata,
          documentContentTypes,
        ),
      )
    : transformation(
        rawValue as string,
        producers,
        producedBy,
        metadata,
        documentContentTypes,
      );
};

export const genericOnChange = (
  identifier: string,
  list: typeof metadataSections | AssetDto[],
  state: string[],
  setState: (state: string[]) => void,
) => {
  const isSelected = state.includes(identifier);
  if (identifier === SELECT_ALL) {
    setState(
      isSelected ? [] : [SELECT_ALL, ...list.map((item) => item.id.toString())],
    );
  } else {
    const newState = isSelected
      ? state.filter((id) => id !== identifier && id !== SELECT_ALL)
      : [...state, identifier];

    if (newState.length === list.length) {
      newState.push(SELECT_ALL);
    }

    setState(newState);
  }
};

export const getMetadataEntries = (
  assets: AssetDto[],
  producers: ProducerDto[],
  producedBy: ProducedByDto,
  bynder?: BynderMetadataDto,
  documentContentTypes?: Record<string, string>,
): MetadataEntry[] => {
  const sectionsMap: Record<string, MetadataEntry> = {};
  assets.forEach((asset) => {
    metadataSections.forEach(
      ({
        id,
        title,
        transformation,
        isOptionalField,
        isConditionallyRequired = (value) => !value,
      }) => {
        if (EXCLUDED_SECTIONS.has(id)) {
          return;
        }

        const rawValue = asset[id];
        const value = applyTransformation(
          rawValue,
          transformation,
          producers,
          producedBy,
          bynder,
          documentContentTypes,
        );

        if (!value && isOptionalField) {
          return;
        }

        if (!sectionsMap[id]) {
          sectionsMap[id] = {
            id,
            title,
            entries: {},
            hideValues: isOptionalField,
            showWarning: isOptionalField,
          };
        }
        const section = sectionsMap[id];
        const entries = section.entries;
        if (Array.isArray(value)) {
          section.showWarning = section.showWarning || !value.length;
          value.forEach((element) => {
            entries[element] = (entries[element] || 0) + 1;
          });
        } else {
          section.showWarning =
            section.showWarning || isConditionallyRequired(value, asset);
          if (value) {
            entries[value as string] = (entries[value as string] || 0) + 1;
          }
        }
      },
    );
  });
  // Remove sections that are empty and don't have a warning
  return Object.values(sectionsMap).filter(
    (section) => section.showWarning || Object.keys(section.entries).length > 0,
  );
};

export async function blobUrlToFile(
  blobUrl: string,
  fileName: string,
  fileType: string,
): Promise<File> {
  const response = await fetch(blobUrl);
  const blob = await response.blob();
  return new File([blob], fileName, { type: fileType });
}

export const getAriaLabel = (modalState: ModalState) => {
  switch (modalState) {
    case ModalState.AssetNames:
      return "Asset names";
    case ModalState.Comments:
      return "Comments modal";
    case ModalState.SimilarAssets:
      return "Similar assets";
    case ModalState.Image:
      return "Preview assets";
    default:
      return "Opened modal";
  }
};
